import React, { useEffect, useRef, useState } from "react";
import { countryflagCode } from "./countryflagCode";

const getCountryByCode = (countryCode, flagCode) => {
    if (flagCode) {
        return countryflagCode.find(
            (country) =>
                country.countryCode === countryCode && country.flagCode === flagCode
        );
    }

    return countryflagCode.find((country) => country.countryCode === countryCode);
};

export default function ContactNumberField({
    countryCode,
    number,
    onCountryChange,
    onNumberChange,
    maxLength,
    required = false,
    containerClassName = "",
    placeholder = "081234 56789",
    labelClassName = "font-semibold block mb-1",
}) {
    const [isOpen, setIsOpen] = useState(false);
    const [searchQuery, setSearchQuery] = useState("");
    const [selectedFlagCode, setSelectedFlagCode] = useState("");
    const containerRef = useRef(null);
    const searchInputRef = useRef(null);
    const selectedCountry = getCountryByCode(countryCode, selectedFlagCode);

    const filteredCountries = countryflagCode.filter((country) => {
        const query = searchQuery.trim().toLowerCase();
        if (!query) return true;

        return (
            country.countryName.toLowerCase().includes(query) ||
            country.countryCode.toLowerCase().includes(query) ||
            country.countryCode.replace("+", "").includes(query)
        );
    });

    useEffect(() => {
        if (!countryCode) {
            setSelectedFlagCode("");
        }
    }, [countryCode]);

    useEffect(() => {
        const handleClickOutside = (event) => {
            if (containerRef.current && !containerRef.current.contains(event.target)) {
                setIsOpen(false);
                setSearchQuery("");
            }
        };

        document.addEventListener("mousedown", handleClickOutside);
        return () => document.removeEventListener("mousedown", handleClickOutside);
    }, []);

    useEffect(() => {
        if (isOpen) {
            searchInputRef.current?.focus();
        } else {
            setSearchQuery("");
        }
    }, [isOpen]);

    const handleCountrySelect = (country) => {
        setSelectedFlagCode(country.flagCode);

        onCountryChange({
            code: country.countryCode,
            flagCode: country.flagCode,
            country: country.countryName,
        });

        setIsOpen(false);
        setSearchQuery("");
    };
        

    return (
        <div className={containerClassName} ref={containerRef}>
            <label className={labelClassName}>
                Contact Number<span className="text-red-500">*</span>
            </label>

            <div className="flex items-stretch w-full rounded-md border border-gray-300 bg-white overflow-visible">
                <div className="relative flex-shrink-0">
                    <button
                        type="button"
                        className="flex items-center gap-1.5 h-[42px] px-2 sm:px-3 border-r border-gray-300 bg-white hover:bg-gray-50"
                        onClick={() => setIsOpen((prev) => !prev)}
                        aria-haspopup="listbox"
                        aria-expanded={isOpen}
                    >
                        {selectedCountry ? (
                            <img
                                src={selectedCountry.flagUrl}
                                alt={selectedCountry.countryName}
                                className="w-6 h-[18px] object-cover rounded-sm"
                            />
                        ) : (
                            <span className="w-6 h-[18px] flex items-center justify-center text-gray-400 text-xs">
                                {/* + */}
                            </span>
                        )}
                        <span className="text-sm whitespace-nowrap">
                            {selectedCountry ? selectedCountry.countryCode : "+"}
                        </span>
                        <svg
                            className={`w-3 h-3 text-gray-500 transition-transform ${isOpen ? "rotate-180" : ""}`}
                            viewBox="0 0 20 20"
                            fill="currentColor"
                            aria-hidden="true"
                        >
                            <path
                                fillRule="evenodd"
                                d="M5.23 7.21a.75.75 0 011.06.02L10 10.94l3.71-3.71a.75.75 0 111.06 1.06l-4.24 4.25a.75.75 0 01-1.06 0L5.21 8.29a.75.75 0 01.02-1.08z"
                                clipRule="evenodd"
                            />
                        </svg>
                    </button>

                    {isOpen && (
                        <div className="absolute left-0 top-full z-50 mt-1 w-[280px] rounded-md border border-gray-200 bg-white shadow-lg overflow-hidden">
                            <div className="sticky top-0 border-b border-gray-200 bg-white p-2">
                                <input
                                    ref={searchInputRef}
                                    type="text"
                                    className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm outline-none focus:border-gray-400"
                                    placeholder="Search country or code"
                                    value={searchQuery}
                                    onChange={(e) => setSearchQuery(e.target.value)}
                                    onClick={(e) => e.stopPropagation()}
                                />
                            </div>

                            <ul className="max-h-52 overflow-y-auto" role="listbox">
                                {filteredCountries.length > 0 ? (
                                    filteredCountries.map((country) => (
                                        <li key={`${country.flagCode}-${country.countryCode}`}>
                                            <button
                                                type="button"
                                                className={`flex w-full items-center gap-2 px-3 py-2 text-left text-sm hover:bg-gray-50 ${
                                                    selectedFlagCode === country.flagCode
                                                        ? "bg-gray-100"
                                                        : ""
                                                }`}
                                                onClick={() => handleCountrySelect(country)}
                                                role="option"
                                                aria-selected={selectedFlagCode === country.flagCode}
                                            >
                                                <img
                                                    src={country.flagUrl}
                                                    alt={country.countryName}
                                                    className="w-6 h-[18px] object-cover rounded-sm flex-shrink-0"
                                                />
                                                <span className="flex-1 truncate">{country.countryName}</span>
                                                <span className="text-gray-400 flex-shrink-0">
                                                    {country.countryCode}
                                                </span>
                                            </button>
                                        </li>
                                    ))
                                ) : (
                                    <li className="px-3 py-3 text-sm text-gray-500 text-center">
                                        No countries found
                                    </li>
                                )}
                            </ul>
                        </div>
                    )}
                </div>

                <input
                    type="tel"
                    className={`flex-1 min-w-0 h-[42px] px-3 bg-transparent outline-none rounded-r-md ${
                        !countryCode ? "cursor-not-allowed bg-gray-100" : ""
                    }`}
                    placeholder={placeholder}
                    value={number}
                    disabled={!countryCode}
                    maxLength={maxLength}
                    required={required}
                    onChange={(e) => {
                        const value = e.target.value;

                        if (/^[0-9]*$/.test(value)) {
                            onNumberChange(value);
                        }
                    }}
                />
            </div>
        </div>
    );
}